import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, OutLink, StatGrid } from "@/components/site/PageBits";
import { SectionLabel } from "@/components/site/FormBits";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/about-the-host");

const SRC = "https://gregmiceli.com/about";

const MILESTONES = [
  ["56 websites & apps in 2026", "Planned, designed, coded, optimized, and launched 56 custom websites and web applications across the United States, Nicaragua, Costa Rica, Canada and beyond."],
  ["Two Nicaraguan companies, both sold", "Co-founded Destination Nica and built Nicaragua Taxi Services to connect travelers with reliable local drivers."],
  ["Nica Bros", "A Nicaragua-inspired browser arcade game — 1,000+ players in its first 90 days."],
  ["Connecting Tampa Bay", "Founded Tampa Bay Business Network, Networking 4 a Cause and Women's Only Networking — communities that grew to 50,000+ members."],
];

export default function AboutHostPage() {
  useSeo(SEO);
  return (
    <article>
      <PageHero eyebrow="ABOUT THE HOST" title="About the Host" image="/brand/greg.jpg" imageAlt="Greg Miceli with his wife Kat and their dog Nicci"
        subtitle="Hi, I'm Greg Miceli — many people know me as Mr. Positivity. Originally from Queens, New York, I now call San Juan del Sur, Nicaragua home.">
        <OutLink href={SRC} testId="about-external-link" light>Read the full story at gregmiceli.com/about</OutLink>
      </PageHero>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-16 text-slate-700 leading-relaxed text-lg space-y-5">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">From the corporate world to building businesses in Nicaragua</h2>
        <p>My journey has taken me from long hours in the corporate world to building businesses, creating brands, launching websites, producing AI music, and helping entrepreneurs turn ideas into reality. Today I combine creativity, technology, and a passion for helping others grow.</p>
        <p>Over the years I've developed websites, apps, CRM systems, marketing campaigns, and digital solutions for businesses across multiple industries. My mission is simple: help people and businesses present themselves professionally, reach more customers, and create opportunities they never thought possible.</p>
        <p>When I'm not working, you'll likely find me in the gym, exploring the beaches of Nicaragua, or spending time with my wife Kat and our dog Nicci.</p>
        <blockquote className="border-l-4 border-[#F08A3C] pl-5 font-display text-xl text-[#0B2A4A]">Dream big. Stay positive. Take action. Never stop building the life you want. And never give up.</blockquote>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <SectionLabel>Experience you'll learn from</SectionLabel>
        <h2 className="mt-3 mb-8 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Milestones</h2>
        <StatGrid stats={[["56", "Websites built in 2026"], ["2", "Nicaraguan companies sold"], ["1,000+", "Nica Bros players in 90 days"], ["50,000+", "Networking community members"]]} />
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {MILESTONES.map(([t, d]) => (
            <div key={t} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6">
              <h3 className="font-semibold text-lg text-[#0B2A4A]">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">As reported on gregmiceli.com/about. In the course, these are shared as examples to learn from — not as promised results.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="rounded-3xl bg-[#F5ECD7] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <h2 className="text-2xl font-bold text-[#0B2A4A]">Learn with Greg in the 7-day beginners course</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/digital-nomad-beginners-course" className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3.5 font-semibold" data-testid="about-course-cta">View the course <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-6 py-3.5 font-semibold" data-testid="about-contact-cta">Contact</Link>
          </div>
        </div>
      </section>
    </article>
  );
}
